import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Footer } from '../components';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { useGSAPReveal } from '../util/useGSAPReveal';

const myBook = {
  title: 'Designing Scalable Systems',
  subtitle: 'The Art of Growth: Scalable Solutions for Tomorrow',
  frontCover: '/book-cover-front.jpg',
  backCover: '/book-cover-back.jpg',
  detailCover: '/book-cover-detail.jpg',
  description:
    '"Designing Scalable Systems" is a book for engineers who need to think clearly about architecture, infrastructure, scaling, resilience, and the trade-offs that shape long-lived systems.',
  link: 'https://www.amazon.com/Designing-Scalable-Systems-Solutions-Tomorrow/dp/B0CS77KBB2',
};

const bookHighlights = [
  {
    title: 'Architecture thinking',
    description: 'Moves from protocols and service boundaries to the larger design choices that make systems easier to evolve.',
  },
  {
    title: 'Operational depth',
    description: 'Connects scaling, monitoring, security, and cost instead of treating them like separate afterthoughts.',
  },
  {
    title: 'Practical framing',
    description: 'Built to help you reason through real decisions, not just memorize patterns in isolation.',
  },
];

const readerProfiles = [
  'Software architects shaping systems that need to survive growth.',
  'Backend engineers working across APIs, databases, queues, and cloud infrastructure.',
  'Technical leads who need design decisions to stay reliable, secure, and cost-aware.',
];

const bookSchema = {
  '@context': 'https://schema.org',
  '@type': 'Book',
  name: 'Designing Scalable Systems',
  alternateName: 'The Art of Growth: Scalable Solutions for Tomorrow',
  description:
    'A comprehensive guide covering API protocols, software architectures, system design processes, databases, cloud solutions, scaling strategies, monitoring, big data analytics, system security, and cost optimization.',
  author: [
    {
      '@type': 'Person',
      name: 'Asim Hafeez',
      url: 'https://asimhafeez.com',
    },
    {
      '@type': 'Person',
      name: 'Huzaifa Asif',
      url: 'https://www.huzaifa.io/',
    },
  ],
  publisher: {
    '@type': 'Organization',
    name: 'Amazon KDP',
  },
  datePublished: '2024-01-01',
  numberOfPages: 350,
  bookFormat: 'https://schema.org/Hardcover',
  isbn: 'B0CS77KBB2',
  url: 'https://www.amazon.com/Designing-Scalable-Systems-Solutions-Tomorrow/dp/B0CS77KBB2',
  image: 'https://asimhafeez.com/book-cover-front.jpg',
  inLanguage: 'en',
  genre: ['Technology', 'Software Engineering', 'System Design', 'Computer Science'],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    bestRating: '5',
    ratingCount: '50',
  },
  about: [
    'System Design',
    'Scalable Architecture',
    'API Protocols',
    'Microservices',
    'Cloud Computing',
    'Database Management',
    'Kubernetes',
    'Docker',
    'System Security',
    'Cost Optimization',
  ],
};

const publicationsBreadcrumbs = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://asimhafeez.com/' },
  { name: 'Publications', url: 'https://asimhafeez.com/publications' },
]);

interface Chapter {
  num: string;
  title: string;
  desc: string;
  details?: string[];
}

const chapters: Chapter[] = [
  {
    num: '01',
    title: 'API Protocols',
    desc: 'Exploration of API protocols, a critical component in scalability. Covers SOAP, REST, GraphQL, gRPC, and WebSockets.',
    details: [
      'Understanding REST principles and constraints',
      'When to use GraphQL vs REST',
      'Deep dive into gRPC and Protocol Buffers',
      'Real-time communication with WebSockets',
    ],
  },
  {
    num: '02',
    title: 'Software Architectures',
    desc: 'Selecting an architecture that aligns with scale. Covers layered, microservices, event-driven, and service-oriented architectures.',
    details: [
      'Monolithic vs microservices architectures',
      'Event-driven architecture patterns',
      'Service-oriented architecture fundamentals',
      'Layered architecture pros and cons',
    ],
  },
  {
    num: '03',
    title: 'System Design Process',
    desc: 'Guides through requirement gathering, boundaries, high-level design, documentation, and knowledge transfer.',
    details: [
      'Handling requirement gathering and analysis',
      'Defining system boundaries efficiently',
      'Creating high-level vs low-level designs',
      'Navigating technical trade-offs',
    ],
  },
  {
    num: '04',
    title: 'Databases and Data Management',
    desc: 'Focuses on choosing the right database, managing data effectively, and optimizing retrieval under scale.',
    details: [
      'Relational vs NoSQL databases',
      'Understanding the CAP theorem',
      'Database sharding and partitioning strategies',
      'Replication models and caching architectures',
    ],
  },
  {
    num: '05',
    title: 'Cloud Solutions',
    desc: 'Explores the modern cloud landscape across IaaS, PaaS, containerization, orchestration, and automation.',
    details: [
      'Cloud service models: IaaS vs PaaS',
      'Containerization with Docker',
      'Container orchestration with Kubernetes',
      'Infrastructure automation with Ansible',
    ],
  },
  {
    num: '06',
    title: 'Scaling',
    desc: 'Addresses strategies including horizontal and vertical scaling, load balancing, caching, and scale case studies.',
    details: [
      'Vertical vs horizontal scaling',
      'Load balancing concepts and algorithms',
      'Designing for auto-scaling',
      'Handling state in scaled applications',
    ],
  },
  {
    num: '07',
    title: 'Monitoring',
    desc: 'Covers observability, performance monitoring, network insight, analytics, and capacity planning.',
    details: [
      'Application performance monitoring',
      'Network monitoring and alerting',
      'Real-time analytics pipelines',
      'Strategic capacity planning',
    ],
  },
  {
    num: '08',
    title: 'Big Data & Analytics',
    desc: 'Explains how to think about large datasets, analytical tooling, and implementation choices for data-heavy systems.',
    details: [
      'Data warehousing vs data lakes',
      'Processing massive datasets',
      'Real-time stream processing',
      'Applying analytics for business insights',
    ],
  },
  {
    num: '09',
    title: 'System Security',
    desc: 'Explores vulnerabilities, threats, and layered security measures needed to protect systems at scale.',
    details: [
      'Authentication vs authorization models',
      'Encryption at rest and in transit',
      'Securing API endpoints',
      'Common vulnerabilities and mitigations',
    ],
  },
  {
    num: '10',
    title: 'Cost Optimisation',
    desc: 'Discusses managing and reducing infrastructure costs without compromising system quality.',
    details: [
      'Cloud pricing models and reserved instances',
      'Leveraging spot instances for background tasks',
      'Right-sizing infrastructure and resources',
      'Implementing cost monitoring and alerting',
    ],
  },
];

export const PublicationsPage: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [openChapter, setOpenChapter] = useState(0);
  const [showChapterScrollCue, setShowChapterScrollCue] = useState(false);
  const chapterListRef = useRef<HTMLDivElement | null>(null);

  const heroRef = useGSAPReveal({ variant: 'fade-up', start: 'top 95%' });
  const highlightsRef = useGSAPReveal({ variant: 'fade-up', delay: 0.08, start: 'top 95%' });
  const chaptersRef = useGSAPReveal({ variant: 'fade-up', delay: 0.12, start: 'top 95%' });
  const readersRef = useGSAPReveal({ variant: 'fade-up', delay: 0.16, start: 'top 95%' });
  const ctaRef = useGSAPReveal({ variant: 'fade-up', delay: 0.2, start: 'top 95%' });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const node = chapterListRef.current;

    if (!node) {
      return undefined;
    }

    const syncChapterScrollCue = () => {
      const canScrollFurther = node.scrollTop + node.clientHeight < node.scrollHeight - 6;
      setShowChapterScrollCue(canScrollFurther);
    };

    syncChapterScrollCue();
    node.addEventListener('scroll', syncChapterScrollCue, { passive: true });
    window.addEventListener('resize', syncChapterScrollCue);

    return () => {
      node.removeEventListener('scroll', syncChapterScrollCue);
      window.removeEventListener('resize', syncChapterScrollCue);
    };
  }, []);

  const currentChapter = useMemo(() => chapters[openChapter] || chapters[0], [openChapter]);

  return (
    <div>
      <SEO
        title="Designing Scalable Systems — Book by Asim Hafeez"
        description="'Designing Scalable Systems: The Art of Growth' is a comprehensive 350-page guide covering API protocols, software architectures, databases, cloud solutions, scaling, monitoring, big data, security, and cost optimization. Co-authored by Asim Hafeez and Huzaifa Asif. Available on Amazon."
        canonicalUrl="/publications"
        ogImage="/book-cover-front.jpg"
        ogType="book"
        keywords="designing scalable systems, system design book, scalable architecture, API protocols, microservices, cloud computing, database management, software architecture book, asim hafeez book"
        jsonLd={[bookSchema, publicationsBreadcrumbs]}
      />

      <section
        className="publications-page"
        style={{
          minHeight: '100vh',
          background: 'transparent',
          padding: isMobileView ? '92px 20px 96px' : '132px 48px 120px',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <div className="publication-shell">
          <section ref={heroRef} className="publication-hero">
            <div className="publication-hero__panel">
              <div className="publication-hero__glow publication-hero__glow--left" />
              <div className="publication-hero__glow publication-hero__glow--right" />

              <div className="publication-hero__copy">
                <div className="publication-eyebrow">Book publication</div>
                <h1>
                  <span className="publication-hero__title-lockup publication-hero__title-lockup--desktop">
                    <span className="publication-hero__title-line">Designing</span>
                    <span className="publication-hero__title-line publication-hero__title-line--nowrap">Scalable Systems</span>
                  </span>
                  <span className="publication-hero__title-lockup publication-hero__title-lockup--mobile">
                    <span className="publication-hero__title-line">Designing Scalable</span>
                    <span className="publication-hero__title-line">Systems</span>
                  </span>
                </h1>
                <p className="publication-hero__subtitle">{myBook.subtitle}</p>
                <p className="publication-hero__description">{myBook.description}</p>

                <div className="publication-hero__actions">
                  <a href={myBook.link} target="_blank" rel="noopener noreferrer" className="publication-button publication-button--primary">
                    <span>Get It on Amazon</span>
                    <svg className="publication-button__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="7" y1="17" x2="17" y2="7"></line>
                      <polyline points="7 7 17 7 17 17"></polyline>
                    </svg>
                  </a>
                  <a href="#chapters" className="publication-button publication-button--secondary">
                    <span>Explore Chapters</span>
                  </a>
                </div>

                <div className="publication-hero__stats">
                  {[
                    { label: 'Pages', value: '350+' },
                    { label: 'Published', value: '2024' },
                    { label: 'Rating', value: '4.8/5' },
                  ].map((item) => (
                    <div key={item.label} className="publication-hero__stat">
                      <span>{item.value}</span>
                      <p>{item.label}</p>
                    </div>
                  ))}
                </div>

                <div className="publication-hero__authors">
                  Written by Asim Hafeez and <a href="https://www.huzaifa.io/" target="_blank" rel="noopener noreferrer">Huzaifa Asif</a>
                </div>
              </div>

              <div className="publication-hero__visual">
                <div className="publication-cover-stack">
                  <img
                    src={myBook.detailCover}
                    alt="Designing Scalable Systems interior preview"
                    className="publication-cover-stack__detail"
                    loading="lazy"
                  />
                  <img
                    src={myBook.backCover}
                    alt="Designing Scalable Systems back cover"
                    className="publication-cover-stack__back"
                    loading="lazy"
                  />
                  <img
                    src={myBook.frontCover}
                    alt="Designing Scalable Systems front cover"
                    className="publication-cover-stack__front"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          <section ref={highlightsRef} className="publication-highlights">
            <div className="publication-highlights__intro">
              <div className="publication-highlights__copy">
                <div className="publication-section-label">Why this book</div>
                <p>
                  The book is built for engineers who need a clearer way to think about structure, trade-offs, and the operational realities that show up once systems start growing.
                </p>
              </div>

              <div className="publication-highlights__quote">
                <h2>A book built around the decisions that make systems hold up under growth.</h2>
              </div>
            </div>

            <div className="publication-highlights__list">
              {bookHighlights.map((item, index) => (
                <article key={item.title} className="publication-highlight-row">
                  <div className="publication-highlight-card__number">{String(bookHighlights.indexOf(item) + 1).padStart(2, '0')}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section id="chapters" ref={chaptersRef} className="publication-chapters">
            <div className="publication-section-heading">
              <div>
                <div className="publication-section-label">Chapter explorer</div>
                <h2>Open the structure of the book.</h2>
              </div>
              <p>
                Each chapter moves from theory into choices and trade-offs, so the book can be used as both a guided read and a desk reference.
              </p>
            </div>

            <div className="publication-chapters__board">
              <div className="publication-chapters__list-panel">
                <div className="publication-chapters__list-intro">
                  <span>Contents</span>
                </div>

                <div className="publication-chapters__list-wrap">
                  <div ref={chapterListRef} className="publication-chapters__list" role="tablist" aria-label="Book chapters">
                    {chapters.map((chapter, index) => {
                      const isActive = index === openChapter;

                      return (
                        <button
                          key={chapter.num}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={`publication-chapter-button${isActive ? ' is-active' : ''}`}
                          onClick={() => setOpenChapter(index)}
                        >
                          <span className="publication-chapter-button__num">{chapter.num}</span>
                          <span className="publication-chapter-button__body">
                            <span className="publication-chapter-button__title">{chapter.title}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {showChapterScrollCue ? (
                    <div className="publication-chapters__scroll-indicator" aria-hidden="true">
                      <i />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="publication-chapters__focus" role="tabpanel">
                <div className="publication-chapters__eyebrow">Chapter {currentChapter.num}</div>
                <h3>{currentChapter.title}</h3>
                <p>{currentChapter.desc}</p>

                <div className="publication-chapters__summary">
                  <div className="publication-chapters__summary-block">
                    {currentChapter.details?.length ? (
                      <ul className="publication-chapters__points">
                        {currentChapter.details.map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>

                  <p className="publication-chapters__note">
                    This chapter is framed to move from concepts into decisions, so it can work both as a guided read and as a reference when you need to revisit the topic later.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section ref={readersRef} className="publication-readers">
            <div className="publication-readers__image">
              <div className="publication-readers__cover-stack">
                <img
                  src={myBook.backCover}
                  alt="Back cover of Designing Scalable Systems"
                  className="publication-readers__cover publication-readers__cover--back"
                  loading="lazy"
                />
                <img
                  src={myBook.frontCover}
                  alt="Front cover of Designing Scalable Systems"
                  className="publication-readers__cover publication-readers__cover--front"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="publication-readers__content">
              <div className="publication-section-label">Readers</div>
              <h2>Designed for people who work on systems that cannot stay small forever.</h2>
              <p>
                The book is aimed at builders who need clarity around architecture, data, cloud, scaling, and operational discipline once real usage and product pressure start to show up.
              </p>

              <div className="publication-readers__list">
                {readerProfiles.map((profile) => (
                  <div key={profile} className="publication-readers__item">
                    <span />
                    <p>{profile}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section ref={ctaRef} className="publication-cta">
            <div className="publication-cta__panel">
              <div className="publication-cta__eyebrow">From the book</div>
              <blockquote className="publication-cta__quote">
                <p>
                  The quality of a system is determined by the system design process through which the system is developed.
                </p>
                <footer>Asim Hafeez</footer>
              </blockquote>
              <a href={myBook.link} target="_blank" rel="noopener noreferrer" className="publication-button publication-button--primary">
                <span>Buy on Amazon</span>
                <svg className="publication-button__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="7" y1="17" x2="17" y2="7"></line>
                  <polyline points="7 7 17 7 17 17"></polyline>
                </svg>
              </a>
            </div>
          </section>
        </div>
      </section>

      <Footer />

      <style>{`
        .publication-shell {
          max-width: 1220px;
          margin: 0 auto;
        }

        .publication-eyebrow,
        .publication-section-label,
        .publication-chapters__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          color: rgba(245, 245, 245, 0.54);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .publication-eyebrow::before,
        .publication-section-label::before,
        .publication-chapters__eyebrow::before {
          content: '';
          width: 28px;
          height: 1px;
          background: linear-gradient(90deg, rgba(196, 149, 106, 0.95), rgba(196, 149, 106, 0));
        }

        .publication-hero {
          margin-bottom: 132px;
        }

        .publication-hero__panel {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(320px, 0.95fr);
          gap: clamp(32px, 5vw, 56px);
          align-items: center;
          padding: clamp(28px, 5vw, 56px);
          border-radius: 36px;
          border: 1px solid rgba(245, 245, 245, 0.08);
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.015)),
            radial-gradient(circle at top left, rgba(196, 149, 106, 0.08), transparent 40%);
          overflow: hidden;
        }

        .publication-hero__glow {
          position: absolute;
          width: 360px;
          height: 360px;
          border-radius: 50%;
          filter: blur(70px);
          pointer-events: none;
          opacity: 0.5;
        }

        .publication-hero__glow--left {
          top: -100px;
          left: -80px;
          background: rgba(196, 149, 106, 0.12);
        }

        .publication-hero__glow--right {
          right: -120px;
          bottom: -120px;
          background: rgba(255, 255, 255, 0.05);
        }

        .publication-hero__copy,
        .publication-hero__visual {
          position: relative;
          z-index: 1;
        }

        .publication-hero__copy h1 {
          font-size: clamp(42px, 6.2vw, 76px);
          line-height: 0.94;
          letter-spacing: -0.06em;
          margin: 18px 0 18px;
          max-width: 720px;
        }

        .publication-hero__title-lockup {
          display: block;
        }

        .publication-hero__title-lockup--mobile {
          display: none;
        }

        .publication-hero__title-line {
          display: block;
        }

        .publication-hero__title-line--nowrap {
          white-space: nowrap;
        }

        .publication-hero__subtitle {
          color: rgba(245, 245, 245, 0.76);
          font-size: clamp(18px, 2vw, 24px);
          line-height: 1.45;
          font-style: italic;
          margin-bottom: 16px;
        }

        .publication-hero__description {
          max-width: 620px;
          color: rgba(245, 245, 245, 0.62);
          font-size: 16px;
          line-height: 1.78;
          margin-bottom: 28px;
        }

        .publication-hero__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          margin-bottom: 30px;
        }

        .publication-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 48px;
          padding: 16px 34px;
          border-radius: 999px;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          font-weight: 500;
          letter-spacing: 0.01em;
          text-transform: none;
          text-decoration: none;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          transition:
            transform var(--duration-normal) var(--ease-out),
            border-color var(--duration-normal) var(--ease-out),
            background var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out),
            box-shadow var(--duration-normal) var(--ease-out);
        }

        .publication-button--primary {
          border: 1px solid transparent;
          background: #C4956A;
          color: #0A0A0A;
        }

        .publication-button--secondary {
          border: 1px solid rgba(245, 245, 245, 0.1);
          background: rgba(255, 255, 255, 0.02);
          color: #F5F5F5;
        }

        .publication-button__icon {
          flex-shrink: 0;
          transition: transform var(--duration-normal) var(--ease-out);
        }

        .publication-button:hover {
          transform: translateY(-1px);
        }

        .publication-button--primary:hover {
          background: #d0a276;
          box-shadow: 0 14px 28px rgba(196, 149, 106, 0.18);
        }

        .publication-button--secondary:hover {
          border-color: rgba(245, 245, 245, 0.16);
          background: rgba(255, 255, 255, 0.04);
          color: #F5F5F5;
        }

        .publication-button--primary:hover .publication-button__icon {
          transform: translate(2px, -2px);
        }

        .publication-hero__stats {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
          max-width: 520px;
        }

        .publication-hero__stat {
          padding-top: 16px;
          border-top: 1px solid rgba(245, 245, 245, 0.08);
        }

        .publication-hero__stat span {
          display: block;
          margin-bottom: 6px;
          color: var(--brand-primary);
          font-family: var(--font-heading);
          font-size: clamp(28px, 4vw, 40px);
          line-height: 0.95;
          letter-spacing: -0.04em;
        }

        .publication-hero__stat p {
          color: rgba(245, 245, 245, 0.48);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .publication-hero__authors {
          margin-top: 24px;
          color: rgba(245, 245, 245, 0.56);
          font-size: 14px;
        }

        .publication-hero__authors a {
          color: rgba(245, 245, 245, 0.8);
          transition: color var(--duration-normal) var(--ease-out);
        }

        .publication-hero__authors a:hover {
          color: var(--brand-primary);
        }

        .publication-hero__visual {
          display: flex;
          justify-content: center;
        }

        .publication-cover-stack {
          position: relative;
          width: min(100%, 520px);
          min-height: 520px;
        }

        .publication-cover-stack img {
          position: absolute;
          display: block;
          border-radius: 20px;
          border: 1px solid rgba(245, 245, 245, 0.08);
          box-shadow: 0 22px 60px rgba(0, 0, 0, 0.45);
        }

        .publication-cover-stack__front {
          right: 0;
          top: 30px;
          width: clamp(250px, 60%, 340px);
          transform: rotate(-2deg);
          z-index: 3;
        }

        .publication-cover-stack__back {
          left: 18px;
          top: 0;
          width: clamp(190px, 44%, 250px);
          transform: rotate(-8deg);
          z-index: 1;
          opacity: 0.9;
        }

        .publication-cover-stack__detail {
          left: 56px;
          bottom: 12px;
          width: clamp(210px, 48%, 280px);
          transform: rotate(7deg);
          z-index: 2;
        }

        .publication-highlights,
        .publication-chapters,
        .publication-readers {
          margin-bottom: 132px;
        }

        .publication-highlights__intro,
        .publication-section-heading {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(280px, 0.72fr);
          gap: 44px;
          align-items: end;
          margin-bottom: 52px;
        }

        .publication-highlights__quote h2,
        .publication-section-heading h2,
        .publication-readers__content h2,
        .publication-cta__panel h2 {
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.05;
          letter-spacing: -0.045em;
          margin: 0;
        }

        .publication-highlights__copy p,
        .publication-section-heading p,
        .publication-readers__content p,
        .publication-cta__panel p {
          color: rgba(245, 245, 245, 0.62);
          font-size: 15px;
          line-height: 1.75;
        }

        .publication-highlights__copy {
          display: grid;
          gap: 18px;
          align-self: start;
          max-width: 560px;
        }

        .publication-highlights__quote {
          position: relative;
          padding: 20px 0 0 28px;
        }

        .publication-highlights__quote::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 1px;
          height: 100%;
          background: linear-gradient(180deg, rgba(196, 149, 106, 0.55), rgba(196, 149, 106, 0));
        }

        .publication-highlights__quote::after {
          content: '“';
          position: absolute;
          left: 18px;
          top: -10px;
          color: rgba(196, 149, 106, 0.4);
          font-family: var(--font-heading);
          font-size: 64px;
          line-height: 1;
        }

        .publication-highlights__list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid rgba(245, 245, 245, 0.06);
        }

        .publication-highlight-card__number {
          color: rgba(245, 245, 245, 0.36);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .publication-highlight-row {
          display: grid;
          grid-template-columns: 64px minmax(220px, 0.52fr) minmax(0, 1fr);
          gap: 24px;
          align-items: start;
          padding: 26px 0;
          border-bottom: 1px solid rgba(245, 245, 245, 0.06);
        }

        .publication-highlight-row h3 {
          font-size: 26px;
          line-height: 1.08;
          letter-spacing: -0.035em;
          margin: 0;
        }

        .publication-highlight-row p {
          color: rgba(245, 245, 245, 0.58);
          font-size: 15px;
          line-height: 1.74;
          margin: 0;
        }

        .publication-chapters__board {
          display: grid;
          grid-template-columns: minmax(0, 1.12fr) minmax(280px, 0.88fr);
          gap: 0;
          align-items: stretch;
          border-radius: 28px;
          border: 1px solid rgba(245, 245, 245, 0.08);
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.028), rgba(255, 255, 255, 0.014)),
            radial-gradient(circle at top left, rgba(196, 149, 106, 0.06), transparent 42%);
          overflow: hidden;
        }

        .publication-cta__panel {
          border-radius: 28px;
          border: 1px solid rgba(245, 245, 245, 0.08);
          background: rgba(245, 245, 245, 0.02);
        }

        .publication-chapters__list-panel,
        .publication-chapters__focus {
          min-width: 0;
        }

        .publication-chapters__list-panel {
          padding: 28px 0 28px 28px;
          border-right: 1px solid rgba(245, 245, 245, 0.06);
        }

        .publication-chapters__list-intro {
          display: grid;
          gap: 10px;
          padding: 0 28px 18px 0;
          margin-bottom: 8px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.06);
        }

        .publication-chapters__list-intro span,
        .publication-chapters__summary-block > span {
          color: rgba(245, 245, 245, 0.46);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .publication-chapters__focus {
          padding: 34px 34px 34px 38px;
        }

        .publication-chapters__focus h3 {
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1;
          letter-spacing: -0.05em;
          margin: 14px 0 14px;
        }

        .publication-chapters__focus > p {
          max-width: 720px;
          color: rgba(245, 245, 245, 0.64);
          font-size: 16px;
          line-height: 1.78;
          margin-bottom: 22px;
        }

        .publication-chapters__summary {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 240px;
          gap: 32px;
          align-items: start;
        }

        .publication-chapters__summary-block {
          min-width: 0;
        }

        .publication-chapters__note {
          margin: 2px 0 0;
          padding-left: 22px;
          border-left: 1px solid rgba(196, 149, 106, 0.28);
          color: rgba(245, 245, 245, 0.54);
          font-size: 14px;
          line-height: 1.78;
        }

        .publication-chapters__list-wrap {
          position: relative;
        }

        .publication-chapters__points {
          display: grid;
          gap: 14px;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .publication-chapters__points li {
          position: relative;
          padding-left: 18px;
          color: rgba(245, 245, 245, 0.74);
          font-size: 14px;
          line-height: 1.72;
        }

        .publication-chapters__points li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 10px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--brand-primary);
        }

        .publication-chapters__list {
          display: flex;
          flex-direction: column;
          gap: 0;
          max-height: 472px;
          padding-bottom: 12px;
          padding-right: 10px;
          overflow-y: auto;
          scrollbar-width: none;
        }

        .publication-chapters__list::-webkit-scrollbar {
          display: none;
        }

        .publication-chapters__scroll-indicator {
          position: absolute;
          left: 50%;
          bottom: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          transform: translateX(-50%);
          opacity: 0.72;
          pointer-events: none;
          transition: opacity var(--duration-normal) var(--ease-out);
        }

        .publication-chapters__scroll-indicator i {
          width: 10px;
          height: 10px;
          border-right: 1px solid rgba(196, 149, 106, 0.88);
          border-bottom: 1px solid rgba(196, 149, 106, 0.88);
          transform: rotate(45deg);
          filter: drop-shadow(0 0 10px rgba(196, 149, 106, 0.16));
        }

        .publication-chapter-button {
          display: grid;
          grid-template-columns: 40px minmax(0, 1fr);
          gap: 18px;
          width: 100%;
          padding: 18px 28px 18px 0;
          border: none;
          border-top: 1px solid rgba(245, 245, 245, 0.06);
          background: transparent;
          text-align: left;
          cursor: pointer;
          transition:
            background var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out),
            padding-left var(--duration-normal) var(--ease-out);
        }

        .publication-chapter-button:first-child {
          border-top: none;
        }

        .publication-chapter-button__body {
          min-width: 0;
        }

        .publication-chapter-button:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .publication-chapter-button.is-active {
          padding-left: 16px;
          background: linear-gradient(90deg, rgba(196, 149, 106, 0.12), rgba(196, 149, 106, 0.02) 72%, transparent);
        }

        .publication-chapter-button__num {
          color: rgba(245, 245, 245, 0.42);
          font-family: var(--font-mono);
          font-size: 12px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .publication-chapter-button__title {
          color: rgba(245, 245, 245, 0.82);
          font-family: var(--font-heading);
          font-size: 21px;
          line-height: 1.22;
          letter-spacing: -0.02em;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .publication-chapter-button.is-active .publication-chapter-button__num,
        .publication-chapter-button.is-active .publication-chapter-button__title {
          color: var(--brand-primary);
        }

        .publication-readers {
          display: grid;
          grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1fr);
          gap: 28px;
          align-items: center;
        }

        .publication-readers__image {
          position: relative;
          min-height: 560px;
          overflow: visible;
        }

        .publication-readers__cover-stack {
          position: relative;
          width: min(100%, 480px);
          min-height: 500px;
          margin: 18px auto;
        }

        .publication-readers__cover {
          position: absolute;
          display: block;
          border-radius: 18px;
          border: 1px solid rgba(245, 245, 245, 0.08);
          box-shadow: 0 22px 48px rgba(0, 0, 0, 0.36);
        }

        .publication-readers__cover--back {
          left: 8px;
          top: 0;
          width: min(74%, 320px);
          transform: rotate(-7deg);
          opacity: 0.88;
        }

        .publication-readers__cover--front {
          right: 0;
          bottom: 0;
          width: min(79%, 348px);
          transform: rotate(4deg);
          z-index: 1;
        }

        .publication-readers__content p {
          margin: 14px 0 20px;
        }

        .publication-readers__list {
          display: grid;
          gap: 14px;
        }

        .publication-readers__item {
          display: grid;
          grid-template-columns: 12px minmax(0, 1fr);
          gap: 14px;
          align-items: start;
          padding-top: 14px;
          border-top: 1px solid rgba(245, 245, 245, 0.06);
        }

        .publication-readers__item span {
          width: 6px;
          height: 6px;
          margin-top: 10px;
          border-radius: 50%;
          background: var(--brand-primary);
        }

        .publication-readers__item p {
          margin: 0;
          color: rgba(245, 245, 245, 0.72);
        }

        .publication-cta {
          padding-top: 40px;
        }

        .publication-cta__panel {
          position: relative;
          padding: clamp(34px, 6vw, 72px) clamp(28px, 6vw, 64px);
          text-align: center;
          background:
            linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.015)),
            radial-gradient(circle at top, rgba(196, 149, 106, 0.09), transparent 50%);
          overflow: hidden;
        }

        .publication-cta__eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          color: rgba(245, 245, 245, 0.46);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }

        .publication-cta__eyebrow::before {
          content: '';
          width: 36px;
          height: 1px;
          background: linear-gradient(90deg, rgba(196, 149, 106, 0.95), rgba(196, 149, 106, 0));
        }

        .publication-cta__quote {
          position: relative;
          max-width: 1100px;
          margin: 28px auto 34px;
          padding: 0 24px;
          border: 0;
        }

        .publication-cta__quote p {
          position: relative;
          max-width: 980px;
          margin: 0 auto;
          color: rgba(245, 245, 245, 0.86);
          font-family: var(--font-heading);
          font-size: clamp(24px, 3vw, 42px);
          font-weight: 400;
          line-height: 1.3;
          letter-spacing: -0.02em;
          text-wrap: balance;
        }

        .publication-cta__quote p::before,
        .publication-cta__quote p::after {
          position: absolute;
          color: rgba(196, 149, 106, 0.78);
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(42px, 5vw, 72px);
          line-height: 1;
        }

        .publication-cta__quote p::before {
          content: '“';
          left: -0.5em;
          top: -0.08em;
        }

        .publication-cta__quote p::after {
          content: '”';
          right: -0.32em;
          bottom: -0.28em;
        }

        .publication-cta__quote footer {
          margin-top: 18px;
          color: rgba(196, 149, 106, 0.88);
          font-family: var(--font-mono);
          font-size: 12px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        @media (max-width: 1100px) {
          .publication-hero__panel,
          .publication-highlights__intro,
          .publication-section-heading,
          .publication-readers,
          .publication-chapters__board,
          .publication-chapters__summary {
            grid-template-columns: 1fr;
          }

        }

        @media (max-width: 900px) {
          .publication-cover-stack {
            min-height: 420px;
          }

          .publication-cover-stack__front {
            width: min(66%, 300px);
          }

          .publication-cover-stack__back {
            width: min(44%, 210px);
          }

          .publication-cover-stack__detail {
            width: min(48%, 230px);
          }

          .publication-hero__stats {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            max-width: 100%;
          }
        }

        @media (max-width: 768px) {
          .publication-hero {
            margin-bottom: 92px;
          }

          .publication-hero__panel {
            padding: 24px;
            border-radius: 28px;
            gap: 24px;
          }

          .publication-hero__visual {
            order: -1;
          }

          .publication-hero__copy {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
            width: 100%;
          }

          .publication-hero__copy .publication-eyebrow {
            justify-content: flex-start;
          }

          .publication-hero__copy h1 {
            font-size: 36px;
            line-height: 0.98;
            margin-bottom: 14px;
            max-width: 100%;
          }

          .publication-hero__title-lockup--desktop {
            display: none;
          }

          .publication-hero__title-lockup--mobile {
            display: block;
          }

          .publication-hero__subtitle {
            font-size: 18px;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
          }

          .publication-hero__description {
            max-width: 100%;
            font-size: 15px;
            line-height: 1.72;
            margin-bottom: 22px;
          }

          .publication-hero__actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            margin-left: 0;
            margin-right: 0;
          }

          .publication-button {
            width: 100%;
          }

          .publication-cover-stack {
            width: min(100%, 340px);
            min-height: 320px;
            margin: 0 auto;
          }

          .publication-cover-stack__front {
            right: 4px;
            top: 28px;
            width: min(66%, 228px);
          }

          .publication-cover-stack__back {
            left: 4px;
            width: min(42%, 150px);
          }

          .publication-cover-stack__detail {
            left: 30px;
            bottom: 0;
            width: min(46%, 165px);
          }

          .publication-hero__stat {
            text-align: left;
          }

          .publication-hero__stat span {
            font-size: 34px;
          }

          .publication-hero__authors {
            text-align: left;
          }

          .publication-highlights,
          .publication-chapters,
          .publication-readers {
            margin-bottom: 92px;
          }

          .publication-highlights__intro,
          .publication-section-heading,
          .publication-readers__content {
            text-align: left;
          }

          .publication-highlights__quote {
            padding: 10px 0 0 20px;
          }

          .publication-highlights__quote::after {
            left: 10px;
            top: -6px;
            font-size: 46px;
          }

          .publication-highlights__intro .publication-section-label,
          .publication-section-heading .publication-section-label,
          .publication-readers__content .publication-section-label {
            justify-content: flex-start;
          }

          .publication-cta__panel {
            padding: 22px;
          }

          .publication-cta__eyebrow {
            justify-content: center;
          }

          .publication-cta__quote {
            max-width: 100%;
            margin: 18px auto 24px;
            padding: 0 14px;
          }

          .publication-cta__quote p {
            font-size: 22px;
            line-height: 1.34;
          }

          .publication-cta__quote p::before {
            left: -0.28em;
            top: -0.04em;
          }

          .publication-cta__quote p::after {
            right: -0.18em;
            bottom: -0.18em;
          }

          .publication-highlight-row {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 22px 0;
          }

          .publication-highlight-row h3 {
            font-size: 22px;
          }

          .publication-chapters__board {
            gap: 0;
          }

          .publication-chapters__list-panel {
            padding: 22px 0 0;
            border-right: none;
            border-bottom: 1px solid rgba(245, 245, 245, 0.06);
          }

          .publication-chapters__list-intro {
            padding: 0 22px 18px;
            margin-bottom: 0;
          }

          .publication-chapter-button {
            grid-template-columns: 42px minmax(0, 1fr);
            gap: 12px;
            padding: 16px 22px;
          }

          .publication-chapter-button__title {
            font-size: 16px;
          }

          .publication-chapter-button__desc {
            font-size: 13px;
          }

          .publication-chapters__focus {
            padding: 22px;
          }

          .publication-chapters__focus h3 {
            font-size: 34px;
          }

          .publication-chapters__points {
            gap: 10px;
          }

          .publication-chapters__summary {
            gap: 20px;
          }

          .publication-readers__list {
            max-width: 100%;
            margin: 0;
            text-align: left;
          }

          .publication-readers__image {
            min-height: 420px;
          }

          .publication-readers__cover-stack {
            width: min(100%, 336px);
            min-height: 350px;
            margin: 20px auto;
          }

          .publication-readers__cover--back {
            left: 8px;
            width: min(68%, 208px);
          }

          .publication-readers__cover--front {
            right: 0;
            width: min(76%, 238px);
          }
        }
      `}</style>
    </div>
  );
};

export default PublicationsPage;
